import { memo, useState, useMemo } from 'react';
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import CustomXAxisTick from '../CustomXAxisTick';
import CustomYAxisTick from '../CustomYAxisTick';
import styled from 'styled-components';
import {
  BAR_CHART_MIN_POINT_SIZE,
  BAR_CHART_STROKE_WIDTH,
  X_AXIS_DEFAULT_HEIGHT,
} from '../constants/utils';
import { SingleSelectMenu } from '../../Dropdown';
import { globalFormat } from '../../../utils/formatting';
import { theme } from '../../../styles/theme';
import ResponsiveChartWrapper from '../ResponsiveChartWrapper';
import { useYAxisWidth } from '../hooks';
import { ChartMode } from '../types';
import { assertCondition } from '../../../utils/types';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import { useFloatingLayerContext } from '../../FloatingManager';
import { useChartContext } from '../ChartContext';

const TooltipContainer = styled.div`
  opacity: 0.8;
  border-radius: 2px;
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.15);
  background-color: rgba(0, 0, 0, 0.8);
  padding: 4px 8px;
`;

const TooltipText = styled.div`
  font-size: 12px;
  color: #ffffff;
`;

const getFixedNumber = (value: number) => {
  if (value === 0) {
    return 0;
  } else if (!value) {
    return 'N/A';
  } else {
    return globalFormat.formatNumber(value, {
      style: 'decimal',
      decimalPlace: 4,
    });
  }
};

const valueRange: any = {
  correlation: [-1, 1],
  marginOfError: [0, 1],
  ppv: [0, 'auto'],
  npv: [0, 'auto'],
  pat: [0, 'auto'],
  sensitivity: [0, 'auto'],
  specificity: [0, 'auto'],
};

export default memo(({ analysisId, data, freqIndex, setFreqIndex }: any) => {
  const { chartData } = useChartContext();
  assertCondition(chartData.queryType === QueryType.Prediction);
  const { correlationKinds, selectedValue } = chartData.data;
  const [currentKind, setKind] = useState(selectedValue);
  const setCurrentKind = (value: string) => {
    setKind(value);
    return;
  };

  const chartInterval =
    data[currentKind].length <= 8
      ? 0
      : Math.floor(data[currentKind].length / 8);

  const tooltipZIndex = useFloatingLayerContext();

  const domain = useMemo(() => valueRange[currentKind], [currentKind]);

  // Calculate the Y axis width dynamically
  const yAxisWidth = useYAxisWidth();

  return (
    <div>
      <SingleSelectMenu
        options={correlationKinds.map((option: any) => ({
          value: option.key,
          text: option.display,
        }))}
        value={currentKind}
        onChange={setCurrentKind}
      />
      <ResponsiveChartWrapper analysisId={analysisId} mode={ChartMode.Build}>
        <ResponsiveContainer id={analysisId}>
          <RechartsBarChart
            className={'PredictionBarChart'}
            data={data[currentKind]}
            margin={{ top: 15, right: 5, bottom: 10, left: 15 }}
            barCategoryGap={'12%'}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey={'name'}
              tickSize={0}
              tick={({ ...props }) => <CustomXAxisTick {...props} />}
              height={X_AXIS_DEFAULT_HEIGHT}
              interval={chartInterval}
            />
            <YAxis
              padding={{ top: 3, bottom: 0 }}
              hide={false}
              tickSize={0}
              domain={domain}
              allowDataOverflow={true}
              tick={<CustomYAxisTick measureDisplayFormat={null} />}
              axisLine={false} // Whether to display the Y axis vertical line or not
              tickCount={5}
              tickMargin={10}
              width={yAxisWidth}
            />
            <Tooltip
              active={true}
              wrapperStyle={{ zIndex: tooltipZIndex }}
              isAnimationActive={false}
              content={(props: any) => {
                const { label } = props;
                const selectedLabel = data[currentKind].find(
                  (obj: any) => obj.name === label,
                );
                const labelKind = correlationKinds.find(
                  (kind: any) => kind.key === currentKind,
                );
                return (
                  <TooltipContainer>
                    <TooltipText>{label}</TooltipText>
                    <div style={{ display: 'flex' }}>
                      <TooltipText>{labelKind?.display}:</TooltipText>
                      <TooltipText
                        style={{ fontWeight: 600, paddingLeft: '4px' }}
                      >
                        {getFixedNumber(selectedLabel?.[currentKind])}
                      </TooltipText>
                    </div>
                  </TooltipContainer>
                );
              }}
            />
            <Bar
              isAnimationActive={false}
              name={currentKind}
              dataKey={currentKind}
              fill={'blue'}
              strokeDasharray={'1 0'}
              minPointSize={BAR_CHART_MIN_POINT_SIZE}
              strokeWidth={BAR_CHART_STROKE_WIDTH}
              barSize={12}
              style={{ cursor: 'pointer' }}
              onClick={(_: any, barIndex: any) => setFreqIndex(barIndex)}
            >
              {data[currentKind].map((_: any, index: number) => (
                <Cell
                  key={`cell-${index}`}
                  fill={index !== freqIndex ? theme.colors.mainBlue : 'black'}
                />
              ))}
            </Bar>
          </RechartsBarChart>
        </ResponsiveContainer>
      </ResponsiveChartWrapper>
    </div>
  );
});
